import styled from '@emotion/styled';
import { useLayoutEffect, useRef, useState } from 'react';
import { findContentEdges } from './contentEdges';
import ListBanners, { BannerFrame } from '@/components/common/organism/ListBanners';
import BannerCarousel, { type CarouselBanner } from '@/components/common/organism/BannerCarousel';
import { usePageBanners } from './usePageBanners';
import { claudeTokens } from '@/styles/claudeTokens';
import type { BannerPlacement } from './pageBannerRoutes';

/**
 * 앱 본문 공용 자리(App.tsx `#app-content` 맨 위·맨 아래) — 화면이 자리를 직접 두지 않는 페이지(상세·마이페이지·검색 등)에 쓴다.
 * 폭: 새 여백을 만들지 않고 그 화면 본문 블록(카드·흰 컨테이너)의 좌우 끝선을 따른다(UI 가이드 6장 "끼워 넣는 블록", contentEdges.ts).
 *   본문을 아직 못 찾았으면(불러오는 중) 앱 본문 폭 그대로다.
 * 위: PC 20px·폰 8px(목록 흰 컨테이너 위 간격과 같음) — 아래 화면은 자기 위 여백을 그대로 둔다.
 * 아래: 본문 뒤 PC 16px·폰 8px, PC 는 푸터와 16px(폰은 앱 본문 아래 16px 이 만든다).
 */
const AppSlot = styled.div<{ placement: BannerPlacement }>`
    margin: ${p => p.placement === 'top' ? '20px 0 0' : '16px 0 16px'};
    @media (max-width: 768px) {
        margin: 8px 0 0;
    }
`;

/** 홈 자리 틀 — 바로 아래 홈 메인 배너(HeroBanner HeroWrap)와 같은 모서리·테두리(PC 반경 lg, 폰 md, 1px 선) */
const HomeFrame = styled(BannerFrame)`
    border: 1px solid ${claudeTokens.color.border};
    border-radius: ${claudeTokens.radius.lg};
    @media (max-width: 768px) {
        border-radius: ${claudeTokens.radius.md};
    }
`;

/**
 * 페이지 상단·하단 배너 한 자리 — 지금 경로의 페이지 위치 배너 + "모든 페이지" 배너(우선순위 순). 없으면 아무것도 그리지 않는다(여백 0).
 * - `app`: 앱 공용 자리. 자리를 직접 두는 화면(목록·홈)과 빠지는 화면(결제·폼·로그인·AI)에서는 비킨다.
 * - `list`: 목록 컨테이너 안(필터 아래·목록 뒤) — 목록 배너 간격(ListBanners) 그대로.
 * - `home`: 홈 섹션 줄 맨 위·맨 아래 — 홈 메인 배너와 같은 폭, 간격은 홈 섹션 간격.
 * 어느 화면에서 빼는지·어느 위치를 묻는지는 pageBannerRoutes.ts 한 곳에서 정한다.
 */
/** 앱 공용 자리 — 옆 본문 블록의 끝선을 재서 좌우 margin 으로 맞춘다(화면 폭·내용이 바뀌면 다시 잰다) */
function AppPageBanner({ placement, banners }: { placement: BannerPlacement; banners: CarouselBanner[] }) {
    const ref = useRef<HTMLDivElement>(null);
    const [inset, setInset] = useState<{ l: number; r: number } | null>(null);
    useLayoutEffect(() => {
        const slot = ref.current;
        const parent = slot?.parentElement;
        if (!slot || !parent) return;
        let frame = 0;
        const measure = () => {
            const box = parent.getBoundingClientRect();
            const cs = getComputedStyle(parent);
            const padL = parseFloat(cs.paddingLeft) || 0;
            const padR = parseFloat(cs.paddingRight) || 0;
            const contentL = box.left + padL;
            const contentR = box.right - padR;
            const edges = findContentEdges(slot, placement, contentR - contentL);
            if (!edges) return;
            // 앱 본문 상자 밖으로는 나가지 않는다
            const next = {
                l: Math.max(-padL, Math.round(edges.left - contentL)),
                r: Math.max(-padR, Math.round(contentR - edges.right)),
            };
            setInset(prev => (prev && prev.l === next.l && prev.r === next.r ? prev : next));
        };
        const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
        measure();
        const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
        resize?.observe(parent);
        // 화면 내용이 늦게 그려지면(불러오기 뒤) 다시 잰다 — 배너 자기 안의 변화(넘김)는 무시
        const mutation = new MutationObserver(records => {
            if (records.some(r => !slot.contains(r.target))) schedule();
        });
        mutation.observe(parent, { childList: true, subtree: true });
        window.addEventListener('resize', schedule);
        return () => { cancelAnimationFrame(frame); resize?.disconnect(); mutation.disconnect(); window.removeEventListener('resize', schedule); };
    }, [placement]);
    return (
        <AppSlot ref={ref} placement={placement} data-page-banners={placement} style={inset ? { marginLeft: inset.l, marginRight: inset.r } : undefined}>
            <BannerFrame><BannerCarousel banners={banners} noSwipeBack /></BannerFrame>
        </AppSlot>
    );
}

export default function PageBannerSlot({ placement, variant }: { placement: BannerPlacement; variant: 'app' | 'list' | 'home' }) {
    const { target, top, bottom } = usePageBanners();
    if (target.excluded) return null;
    if (variant === 'app' && target.inPage) return null;
    const banners = (placement === 'top' ? top : bottom).filter(b => b.imageUrl) as CarouselBanner[];
    if (!banners.length) return null;
    if (variant === 'list') return <ListBanners banners={banners} placement={placement} />;
    if (variant === 'home') return <div data-page-banners={placement}><HomeFrame><BannerCarousel banners={banners} noSwipeBack /></HomeFrame></div>;
    return <AppPageBanner placement={placement} banners={banners} />;
}
